import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { X, Upload, FileSpreadsheet, Link as LinkIcon, Shuffle, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const BulkImportModal = ({ onClose }) => {
  const [importType, setImportType] = useState('file'); // 'file' or 'url'
  const [file, setFile] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [randomize, setRandomize] = useState(false);
  const [callers, setCallers] = useState([]);
  const [selectedCallers, setSelectedCallers] = useState([]);
  
  // Background import tracking
  const [importJob, setImportJob] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchCallers();
    
    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const fetchCallers = async () => {
    try {
      const response = await api.getUsers('caller');
      setCallers(response.data);
    } catch (error) {
      console.error('Error fetching callers:', error);
    }
  };

  const pollImportStatus = async (jobId) => {
    try {
      const response = await api.getImportStatus(jobId);
      const job = response.data;
      setImportJob(job);
      
      if (job.status === 'completed') {
        clearInterval(pollIntervalRef.current);
        setIsPolling(false);
        setLoading(false);
        toast.success(job.message || 'Leads imported successfully!');
      } else if (job.status === 'failed') {
        clearInterval(pollIntervalRef.current);
        setIsPolling(false);
        setLoading(false);
        toast.error(job.error || 'Import failed');
      }
    } catch (error) {
      console.error('Error polling import status:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    
    if (importType === 'file' && !file) {
      toast.error('Please select a file');
      return;
    }
    
    if (importType === 'url' && !googleSheetUrl) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }
    
    if (randomize && selectedCallers.length === 0) {
      toast.error('Please select at least one caller for random assignment');
      return;
    }

    setLoading(true);
    setImportJob(null);
    
    try {
      let response;
      if (importType === 'file') {
        response = await api.importLeads(file);
        
        // Check if it's a background job
        if (response.data.job_id) {
          setImportJob({
            job_id: response.data.job_id,
            status: 'queued',
            progress: 0
          });
          setIsPolling(true);
          
          // Start polling for status
          pollIntervalRef.current = setInterval(() => {
            pollImportStatus(response.data.job_id);
          }, 1500); // Poll every 1.5 seconds
          
          toast.info('Import started in background. You can continue using other modules.');
        } else {
          // Legacy response (immediate completion)
          toast.success(response.data.message);
          setLoading(false);
          onClose();
        }
      } else {
        response = await api.importLeadsFromUrl(
          googleSheetUrl,
          randomize ? selectedCallers : null,
          randomize
        );
        
        if (randomize && response.data.assignments) {
          const summary = response.data.assignments
            .map(a => `${a.name}: ${a.count} leads`)
            .join(', ');
          toast.success(`${response.data.message}. Distribution: ${summary}`);
        } else {
          toast.success(response.data.message);
        }
        setLoading(false);
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import leads');
      setLoading(false);
    }
  };

  const handleCallerToggle = (callerId) => {
    setSelectedCallers(prev =>
      prev.includes(callerId)
        ? prev.filter(id => id !== callerId)
        : [...prev, callerId]
    );
  };

  const handleSelectAllCallers = () => {
    if (selectedCallers.length === callers.length) {
      setSelectedCallers([]);
    } else {
      setSelectedCallers(callers.map(c => c.id));
    }
  };

  const handleCloseWithWarning = () => {
    if (isPolling) {
      // Import will continue in background
      toast.info('Import will continue in background. Check the import history for status.');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold font-headings text-slate-900">Import Leads</h2>
            <p className="text-slate-600 font-body text-sm mt-1">Upload file or paste Google Sheets link</p>
          </div>
          <button
            data-testid="close-import-modal"
            onClick={handleCloseWithWarning}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X size={24} className="text-slate-600" />
          </button>
        </div>
        
        {/* Progress Indicator for Background Import */}
        {importJob && (
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              {importJob.status === 'queued' && (
                <>
                  <Loader2 className="animate-spin text-blue-600" size={20} />
                  <span className="font-medium text-slate-700">Queued for processing...</span>
                </>
              )}
              {importJob.status === 'processing' && (
                <>
                  <Loader2 className="animate-spin text-blue-600" size={20} />
                  <span className="font-medium text-slate-700">Processing leads...</span>
                </>
              )}
              {importJob.status === 'completed' && (
                <>
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-medium text-green-700">Import completed!</span>
                </>
              )}
              {importJob.status === 'failed' && (
                <>
                  <AlertCircle className="text-red-600" size={20} />
                  <span className="font-medium text-red-700">Import failed</span>
                </>
              )}
            </div>
            
            {/* Progress bar */}
            {(importJob.status === 'queued' || importJob.status === 'processing') && (
              <div className="w-full bg-slate-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${importJob.progress || 0}%` }}
                ></div>
              </div>
            )}
            
            {/* Stats */}
            {importJob.imported_count > 0 && (
              <p className="text-sm text-slate-600 mt-2">
                {importJob.imported_count} leads imported
                {importJob.skipped_count > 0 && ` (${importJob.skipped_count} skipped)`}
              </p>
            )}
            
            {importJob.error && (
              <p className="text-sm text-red-600 mt-2">{importJob.error}</p>
            )}
            
            <p className="text-xs text-slate-500 mt-2">
              💡 You can close this modal - import will continue in background
            </p>
          </div>
        )}

        {/* Content */}
        <form onSubmit={handleImport} className="p-6 space-y-6">
          {/* Import Type Tabs */}
          <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
            <button
              type="button"
              data-testid="import-type-file"
              onClick={() => setImportType('file')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all font-body text-sm ${
                importType === 'file'
                  ? 'bg-white text-slate-900 shadow-sm font-medium'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Upload size={16} />
              <span>Upload File</span>
            </button>
            <button
              type="button"
              data-testid="import-type-url"
              onClick={() => setImportType('url')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-all font-body text-sm ${
                importType === 'url'
                  ? 'bg-white text-slate-900 shadow-sm font-medium'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <LinkIcon size={16} />
              <span>Google Sheets</span>
            </button>
          </div>

          {/* File Upload */}
          {importType === 'file' && (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-300 hover:border-slate-400'
              }`}
            >
              <input
                type="file"
                id="file-upload"
                data-testid="file-upload-input"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center">
                  {file ? (
                    <>
                      <FileSpreadsheet size={48} className="text-green-600 mb-3" />
                      <p className="text-slate-900 font-medium font-body mb-1">{file.name}</p>
                      <p className="text-slate-500 text-sm font-body">{(file.size / 1024).toFixed(2)} KB</p>
                    </>
                  ) : (
                    <>
                      <Upload size={48} className="text-slate-400 mb-3" />
                      <p className="text-slate-900 font-medium font-body mb-1">Click to upload or drag and drop</p>
                      <p className="text-slate-500 text-sm font-body">Excel (.xlsx, .xls) or CSV files</p>
                    </>
                  )}
                </div>
              </label>
            </div>
          )}

          {/* Google Sheets URL */}
          {importType === 'url' && (
            <div>
              <label htmlFor="google-sheet-url" className="block text-sm font-medium text-slate-700 font-body mb-2">
                Google Sheets URL
              </label>
              <input
                type="url"
                id="google-sheet-url"
                data-testid="google-sheet-url-input"
                value={googleSheetUrl}
                onChange={(e) => setGoogleSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full px-4 py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                required={importType === 'url'}
              />
              <p className="text-xs text-slate-500 font-body mt-2">
                Make sure the sheet is publicly accessible or anyone with the link can view
              </p>
            </div>
          )}

          {/* Randomizer Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <input
                type="checkbox"
                id="randomize"
                data-testid="randomize-checkbox"
                checked={randomize}
                onChange={(e) => setRandomize(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              />
              <div className="flex-1">
                <label htmlFor="randomize" className="flex items-center gap-2 font-semibold text-slate-900 font-body cursor-pointer">
                  <Shuffle size={18} className="text-blue-600" />
                  <span>Auto-distribute leads randomly</span>
                </label>
                <p className="text-sm text-slate-600 font-body mt-1">
                  Automatically assign imported leads to selected callers
                </p>
              </div>
            </div>

            {randomize && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-slate-700 font-body">Select Callers</p>
                  <button
                    type="button"
                    data-testid="select-all-callers"
                    onClick={handleSelectAllCallers}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedCallers.length === callers.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-2 bg-white rounded-md p-3 border border-blue-200">
                  {callers.length === 0 ? (
                    <p className="text-sm text-slate-500 font-body text-center py-2">No callers available</p>
                  ) : (
                    callers.map((caller) => (
                      <label
                        key={caller.id}
                        data-testid={`caller-checkbox-${caller.id}`}
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCallers.includes(caller.id)}
                          onChange={() => handleCallerToggle(caller.id)}
                          className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                        />
                        <span className="text-sm font-body text-slate-700">{caller.name}</span>
                        <span className="text-xs text-slate-500 font-body">({caller.email})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* File Format Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h3 className="font-semibold text-slate-900 font-body mb-2">Required Format</h3>
            <p className="text-sm text-slate-700 font-body mb-2">
              Your {importType === 'file' ? 'file' : 'spreadsheet'} must include the following columns:
            </p>
            <ul className="text-sm text-slate-700 font-mono space-y-1 ml-4 list-disc">
              <li>name (required)</li>
              <li>phone (required)</li>
              <li>email (optional)</li>
              <li>company (optional)</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50 transition-all shadow-sm font-body"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="import-submit-button"
              disabled={loading || (importType === 'file' && !file) || (importType === 'url' && !googleSheetUrl)}
              className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Importing...' : 'Import Leads'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkImportModal;